/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The four things an agency asks about its subscription.
 *
 * What it is, what it costs, when it is charged, and on what card. The old
 * strip answered the first two with one plan name and one price, which is
 * the shape of a subscription that is one tier for everybody.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Text, UnstyledButton } from '@/shared/ui';
import { useMediaQuery } from '@/shared/hooks/use-media-query';
import { formatCents } from '@/shared/utils/format-cents';
import { formatLocalizedDate } from '@/shared/utils/date';
import type { SubscriptionLine } from '../seats/seat-reduction';
import {
  cycleTotalCents, daysUntilCharge, monthlyEquivalentCents, nextChargeCents,
  planChips, seatsAtRenewal,
} from './plan-summary';

/** One of the four columns. */
const Column: React.FC<{
  label: string;
  value: React.ReactNode;
  support?: React.ReactNode;
  first: boolean;
  stacked: boolean;
}> = ({ label, value, support, first, stacked }) => (
  <div
    style={{
      display: 'flex', flexDirection: 'column', gap: 'var(--space-1)',
      flex: '1 1 0', minWidth: 0,
      padding: stacked ? 'var(--space-3) 0' : '0 var(--space-5)',
      borderLeft: first || stacked ? 'none' : '1px solid var(--border-ultra-subtle)',
      borderTop: !first && stacked ? '1px solid var(--border-ultra-subtle)' : 'none',
    }}
  >
    <Text
      component="span"
      style={{
        fontSize: 'var(--text-meta)', fontWeight: 'var(--font-semibold)',
        color: 'var(--text-tertiary-soft)',
      }}
    >
      {label}
    </Text>
    <Text
      component="span"
      style={{
        fontFamily: 'var(--font-family-alt-2)', fontSize: 'var(--text-xl)',
        fontWeight: 'var(--font-bold)', color: 'var(--text-primary)',
        letterSpacing: '-0.01em',
      }}
    >
      {value}
    </Text>
    {support && (
      <Text
        component="span"
        style={{
          fontSize: 'var(--text-meta)', lineHeight: 1.4,
          color: 'var(--text-tertiary-soft)',
        }}
      >
        {support}
      </Text>
    )}
  </div>
);

/** One plan of the subscription, named with its seats. */
const Chip: React.FC<{ label: string; seats: number }> = ({ label, seats }) => (
  <Text
    component="span"
    style={{
      display: 'inline-flex', alignItems: 'center', height: 20,
      padding: '0 var(--space-2)', borderRadius: 'var(--radius-full)',
      background: 'var(--brand-beige)', color: 'var(--seed-base)',
      fontSize: 'var(--text-tag)', fontWeight: 'var(--font-bold)',
    }}
  >
    {`${seats} ${label}`}
  </Text>
);

export interface PlanSummaryProps {
  segmentLabel: string;
  items: SubscriptionLine[];
  scheduled: { items: SubscriptionLine[]; effective_at: string } | null;
  priceOf: (planId: string) => number;
  titleOf: (planId: string) => string | null;
  billingFrequency: string | null;
  nextChargeDate: string | null;
  cardBrand: string | null;
  cardLastFour: string | null;
  onChangeCard: () => void;
  /**
   * Opens the plan flow pre-aimed at the yearly cycle of today's plan.
   * Absent when the subscription cannot change cycle: the link is not drawn.
   */
  onSwitchToYearly?: () => void;
  /** In the free trial the next charge is the first one (RN-10, RN-17: a date, never a countdown). */
  trial?: boolean;
  /** The owner asked to cancel: the date is when access ends, and nothing more is charged (BUG-048). */
  cancelScheduled?: boolean;
}

/** Which label names the date: access ends, first charge, or next charge. */
function dateLabelKey(trial: boolean, cancelScheduled: boolean): string {
  if (cancelScheduled) return 'v2.summary.accessUntilLabel';
  return trial ? 'v2.summary.firstChargeLabel' : 'v2.summary.nextChargeLabel';
}

/** The line under the next charge date. */
const NextChargeSupport: React.FC<{
  trial: boolean; days: number | null; reducedTo: string | null;
}> = ({ trial, days, reducedTo }) => {
  const { t } = useTranslation('features/subscription');
  if (trial) return <>{t('v2.summary.trialSupport')}</>;
  return (
    <>
      {days != null && t('v2.summary.inDays', { count: days })}
      {/* The bill that is actually coming, which a scheduled reduction
          has already changed. Quoting today's total would name a
          figure the agency is not going to be charged. */}
      {reducedTo && ` · ${t('v2.summary.afterReduction', { price: reducedTo })}`}
    </>
  );
};

/**
 * The summary card.
 *
 * @param props - What the subscription holds and how it is paid.
 * @returns The four columns.
 */
export const PlanSummary: React.FC<PlanSummaryProps> = ({
  segmentLabel, items, scheduled, priceOf, titleOf, billingFrequency,
  nextChargeDate, cardBrand, cardLastFour, onChangeCard, onSwitchToYearly, trial = false,
  cancelScheduled = false,
}) => {
  const { t, i18n } = useTranslation('features/subscription');
  const stacked = useMediaQuery('(max-width: 639px)');
  const money = (cents: number) => formatCents(cents, { locale: i18n.language });
  const yearly = billingFrequency === 'yearly';
  const cycle = cycleTotalCents(items, priceOf);
  const coming = nextChargeCents(items, scheduled, priceOf);
  const days = daysUntilCharge(nextChargeDate, new Date());
  const chips = planChips(items, titleOf);

  return (
    <div
      data-testid="plan-summary"
      style={{
        display: 'flex', flexDirection: stacked ? 'column' : 'row',
        alignItems: stacked ? 'stretch' : 'flex-start',
        padding: 'var(--space-5)', borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--border-subtle)', background: 'var(--surface-raised)',
      }}
    >
      <Column
        first
        stacked={stacked}
        label={t('v2.summary.planLabel')}
        value={segmentLabel}
        support={(
          <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1-5)' }}>
            {chips.map((chip) => (
              <Chip key={chip.planId} label={chip.label} seats={chip.seats} />
            ))}
          </Box>
        )}
      />
      <Column
        first={false}
        stacked={stacked}
        label={t(yearly ? 'v2.summary.yearlyLabel' : 'v2.summary.monthlyLabel')}
        value={money(cycle)}
        support={yearly
          ? t('v2.summary.perMonthWithDiscount', {
            price: money(monthlyEquivalentCents(cycle, billingFrequency)),
          })
          : (
            <>
              {t('v2.summary.seatsTotal', { count: seatsAtRenewal(items, null) })}
              {onSwitchToYearly && (
                <>
                  {' · '}
                  <UnstyledButton
                    onClick={onSwitchToYearly}
                    style={{
                      background: 'none', cursor: 'pointer', fontSize: 'inherit',
                      color: 'var(--link-text)', textDecoration: 'underline',
                      textUnderlineOffset: 3,
                    }}
                  >
                    {t('v2.summary.switchToYearly')}
                  </UnstyledButton>
                </>
              )}
            </>
          )}
      />
      <Column
        first={false}
        stacked={stacked}
        label={t(dateLabelKey(trial, cancelScheduled))}
        value={nextChargeDate
          ? formatLocalizedDate(nextChargeDate, i18n.language)
          : t('v2.summary.noDate')}
        support={cancelScheduled ? t('v2.summary.cancelScheduledSupport') : (
          <NextChargeSupport
            trial={trial}
            days={days}
            reducedTo={scheduled ? money(coming) : null}
          />
        )}
      />
      <Column
        first={false}
        stacked={stacked}
        label={t('v2.summary.paymentLabel')}
        value={cardLastFour ? `•••• ${cardLastFour}` : t('v2.summary.noCard')}
        support={(
          <>
            {cardBrand ? `${cardBrand} · ` : ''}
            <UnstyledButton
              onClick={onChangeCard}
              style={{
                background: 'none', cursor: 'pointer', fontSize: 'inherit',
                color: 'var(--link-text)', textDecoration: 'underline',
                textUnderlineOffset: 3,
              }}
            >
              {t('v2.summary.changeCard')}
            </UnstyledButton>
          </>
        )}
      />
    </div>
  );
};
